import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { Lock, Mail, Landmark } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { formatApiErrorDetail } from "@/lib/api";

export default function AdminLogin() {
    const { user, login } = useAuth();
    const { t } = useLang();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (user && user.role === "admin") {
        return <Navigate to="/admin" replace />;
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            nav("/admin");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || t.admin.loginFailed);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-[calc(100vh-80px-40px)] flex items-center justify-center bg-[#F7FAFC] py-16 px-6" data-testid="admin-login-page">
            <div className="w-full max-w-md bg-white p-8 md:p-10 rounded-lg border border-gray-200 shadow-sm">
                <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-full bg-[#1A365D] text-white flex items-center justify-center border-2 border-[#DD6B20]">
                        <Landmark size={20} />
                    </div>
                    <div>
                        <h1 className="font-serif text-2xl font-semibold text-[#1A365D]">
                            {t.admin.loginTitle}
                        </h1>
                        <p className="text-xs text-gray-500 mt-1">{t.admin.loginSub}</p>
                    </div>
                </div>

                <form onSubmit={submit} className="space-y-5">
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-gray-600 mb-2 inline-block">
                            {t.admin.email}
                        </span>
                        <div className="relative">
                            <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                type="email"
                                required
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="pl-9 h-11"
                                data-testid="admin-email-input"
                            />
                        </div>
                    </label>
                    <label className="block">
                        <span className="text-xs uppercase tracking-widest font-semibold text-gray-600 mb-2 inline-block">
                            {t.admin.password}
                        </span>
                        <div className="relative">
                            <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <Input
                                type="password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="pl-9 h-11"
                                data-testid="admin-password-input"
                            />
                        </div>
                    </label>

                    {error && (
                        <div
                            className="text-sm text-[#C53030] bg-red-50 border border-red-200 rounded-md p-3"
                            data-testid="admin-login-error"
                        >
                            {error}
                        </div>
                    )}

                    <Button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-[#1A365D] hover:bg-[#2A4365] h-11"
                        data-testid="admin-login-submit"
                    >
                        {loading ? t.admin.signingIn : t.admin.signIn}
                    </Button>
                </form>

                <div className="mt-6 text-center text-xs text-gray-400">
                    <Link to="/" className="hover:text-[#1A365D]">
                        ← Back to public site
                    </Link>
                </div>
            </div>
        </div>
    );
}
